import React, { FunctionComponent, useEffect } from 'react';
import { useSelector } from 'react-redux';

import { ServiceLocation } from 'app/api/@generated/backend';

import { ChooseButton } from 'app/components/choose-button';
import { ChooseContainer } from 'app/components/choose-container';
import { Image } from 'app/components/image';
import { Spinner } from 'app/components/spinner';
import { i18n } from 'app/i18n';
import { ServiceTypeMapper } from 'app/pages/order/mapper/service-type-mapper';
import { OrderStep } from 'app/pages/order/redux/model/order-step';
import { selectSelectedServiceLocationId, selectServiceLocationsOrThrow } from 'app/pages/order/redux/order-selector';
import { houseImageProps } from 'app/resources/images';
import { useAppDispatch } from 'app/hooks/thunk-hooks';
import { selectServiceLocationActionCreator } from 'app/pages/order/redux/action-creators/select-service-location-action-creator';
import { useGoToStep } from 'app/hooks/use-go-to-step';

import {
    StyledContentContainer,
    StyledDwellingUnitNumber,
    StyledImageContainer,
    StyledTextContainer,
    StyledTitle,
} from './choose-service-location-page-content.styled';

const ChooseServiceLocationPageContent: FunctionComponent = () => {
    const serviceLocations = useSelector(selectServiceLocationsOrThrow);
    const serviceLocationId = useSelector(selectSelectedServiceLocationId);
    const dispatch = useAppDispatch();
    const goToStep = useGoToStep();

    useEffect(() => {
        if (serviceLocations.length === 1) {
            if (!serviceLocationId) {
                dispatch(selectServiceLocationActionCreator(serviceLocations[0].siteId));
            }
            goToStep(OrderStep.HOUSING_SELECT, true);
        }
    }, [dispatch, goToStep, serviceLocationId, serviceLocations]);

    const renderTitle = (serviceLocation: ServiceLocation) => {
        const { address } = serviceLocation;

        const { streetAddress, dwellingUnitNumber } = address;

        return (
            <StyledContentContainer>
                <StyledImageContainer>
                    <Image {...houseImageProps} />
                </StyledImageContainer>
                <StyledTextContainer>
                    <StyledTitle variant="paragraph2" component="p" data-hj-suppress={true}>
                        {streetAddress}
                    </StyledTitle>
                    {dwellingUnitNumber && (
                        <StyledDwellingUnitNumber variant="paragraph3" component="p" data-hj-suppress={true}>
                            {dwellingUnitNumber}
                        </StyledDwellingUnitNumber>
                    )}
                </StyledTextContainer>
            </StyledContentContainer>
        );
    };

    const handleClick = (id: number) => {
        if (serviceLocationId !== id) {
            dispatch(selectServiceLocationActionCreator(id));
        }
        goToStep(OrderStep.HOUSING_SELECT);
    };

    if (serviceLocations.length === 1) {
        return <Spinner />;
    }

    return (
        <ChooseContainer
            title={i18n.page.chooseServiceLocation.title}
            subTitle={i18n.page.chooseServiceLocation.subtitle}
        >
            {serviceLocations.map((serviceLocation) => {
                const { services, siteId } = serviceLocation;

                if (!siteId) {
                    throw new Error('Site id must be present');
                }

                return (
                    <ChooseButton
                        key={siteId}
                        title={renderTitle(serviceLocation)}
                        items={services ? ServiceTypeMapper.toSelectButtonItems(services) : []}
                        onClick={() => handleClick(siteId)}
                    />
                );
            })}
        </ChooseContainer>
    );
};

export { ChooseServiceLocationPageContent };
